import { useMemo, useState } from 'react'

import { BiddingLifecycleAction } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { invalidateSpotApprovalRequestQueries } from '~/hooks/bidding/queryKeys'
import { useTransitionBiddingSpotApprovalState } from '~/hooks/bidding/useTransitionBiddingSpotApprovalState'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import type { BidLimits } from '~/models/bidLimits'
import { now as serverNow } from '~/models/clock'
import { useApproverIdentity } from '~/pages/SpotBidding/hooks/useApproverIdentity'
import { buildApproverConfirmNotices } from '~/pages/SpotBidding/models/approverIdentity'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import {
  calcSpotPhase,
  isSpotBeforeDeadline,
} from '~/pages/SpotBidding/models/marketPhase'
import type {
  SpotAreaBreakdownRow,
  SpotBidSummary,
} from '~/pages/SpotBidding/models/spotApprovalDecision'
import {
  buildSpotApprovalChecks,
  buildSpotAreaBreakdown,
  selectRequestBids,
  selectRevalidationBids,
  summarizeSpotBidRows,
} from '~/pages/SpotBidding/models/spotApprovalDecision'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import type { VolumeCountableBid } from '~/pages/SpotBidding/models/spotBidValidation'
import { translateSpotWriteError } from '~/pages/SpotBidding/models/spotBidWrite'

type Params = {
  /**
   * PENDING の request 群 (= 確定時の対象解決元)。 id ではなく確定時にここから引き直す
   * ことで、 modal を開いたまま背景 refetch が走っても stale な `expectedUpdatedAt` を
   * 握らない (= ブロック側 useBlockApprovalDecision / 申請取消 cancelingRequestId と同じ
   * 不変条件)。
   */
  pendingRequests: BiddingSpotApprovalRequest[]
  /**
   * 受渡日内の全札。 承認対象 request の札を切り出すのと、 承認時の再検証母集合
   * (AC-BSP-097 / AC-BSP-080) を抜き出すのに使う。
   */
  bids: SpotBidRow[]
  /**
   * 上限数量(コマ計) の再検証母集合 (AC-BSP-097 / AC-BSP-080)。 受渡日内の有効な通常入札と、
   * 有効なブロック入札の当該コマ数量を足したもの (page が組み、 ブロック側
   * useBlockApprovalDecision と同じものを渡す)。 承認対象の札は PENDING なので通常入札側に
   * 含まれる。
   */
  volumeTotalRows: readonly VolumeCountableBid[]
  /**
   * ブロック入札の一覧 / 明細が未解決か (= volumeTotalRows のブロック入札分を全量と確定できない)。
   * 上限数量(コマ計) の再検証が「見えているブロック入札の範囲での違反なし」 に縮退するため、
   * 上限数量(コマ計) が効く札では fail-closed にする (isStateUnresolved と同じ扱い)。
   */
  isBlockVolumeUnresolved: boolean
  /** settings「入札制限」の解決済み設定値 (= useBidLimits)。 */
  limits: BidLimits
  /** 入札制限の取得に失敗しているか (= 再検証が成立しないので fail-closed にする)。 */
  isLimitsError: boolean
  /**
   * 一覧 / 明細 Get が未解決か (= 受渡日の札を全量把握できていない)。 未解決の request の
   * 札は `bids` から静かに脱落するため、 AC-BSP-097 の再検証が「見えている札の範囲での
   * 違反なし」 に縮退する。 fail-closed にする (= ブロック側 useBlockApprovalDecision の
   * isStateUnresolved と同じ扱い)。
   */
  isStateUnresolved: boolean
  /** 完了済み / 削除済みワークフロー経由か (AC-BSP-058)。 */
  isReadonly: boolean
  /**
   * 導線の出し分けに使う市場フェーズ (= 分単位 tick 由来、 締切 AC-BSP-099)。 確定時は
   * これではなく `targetDate` とサーバー由来の現在時刻で引き直す。
   */
  phase: SpotMarketPhase
  /** 受渡日 (= 締切 AC-BSP-099 の再判定に使う)。 */
  targetDate: Date
  /** 遷移の直前に呼ぶ (= 画面共通の書込エラー表示のクリア)。 */
  onTransitionStart?: () => void
  /** 遷移が成立して対象が PENDING でなくなった時に呼ぶ (= 一覧絞り込みの解除)。 */
  onDecided?: (requestId: string) => void
}

export type SpotApprovalDecision = {
  /** 承認確認 modal の対象 request id (= undefined で非表示)。 */
  approvingRequestId: string | undefined
  /** 差戻しダイアログの対象 request id (= undefined で非表示)。 */
  rejectingRequestId: string | undefined
  openApprove: (request: BiddingSpotApprovalRequest) => void
  openReject: (request: BiddingSpotApprovalRequest) => void
  closeApprove: () => void
  closeReject: () => void
  approve: () => Promise<void>
  reject: (reason: string) => Promise<void>
  isTransitioning: boolean
  /**
   * 承認 / 差戻し を今できない理由 (= バナーのボタンを disabled + tooltip にする文言)。
   * undefined なら操作可。 確定時ガード (ensureDecisionAllowed) と同じ policy を導線側にも
   * 出す唯一の口 (= caller が isReadonly / phase を独立に再構成しない)。
   */
  decisionDisabledReason: string | undefined
  /**
   * 承認 / 差戻し を `phaseAt` の時点でできない理由 (= `decisionDisabledReason` と同じ policy)。
   * まとめて承認 (AC-BSP-118) が確定時にサーバー由来の現在時刻で引き直すのに使い、 可否 policy を
   * 単件と分けて持たない。
   */
  decisionBlockedReasonAt: (phaseAt: SpotMarketPhase) => string | undefined
  /** 承認確認 modal のサマリ (件数 / 買・売合計)。 */
  summary: SpotBidSummary
  /** 承認確認 modal のエリア別内訳 (AC-BSP-097)。 */
  areaBreakdown: SpotAreaBreakdownRow[]
  /** 承認確認 modal に積む違反明細 (AC-BSP-097 の再検証結果)。 */
  violationSections: ViolationSection[]
  /** 違反があるか (= 承認確定ボタンを非活性にする、 AC-BSP-097)。 */
  hasViolation: boolean
  /**
   * 承認確認 modal に積む注意 (= 売買逆転チェック結果、 AC-BSP-097)。 spec §2 に従い
   * 承認はブロックしない (hasViolation に寄与しない)。
   */
  warningSections: ViolationSection[]
  /** 承認確認 modal に出す承認者でない・自己承認の再確認 (AC-BSP-098。 確定は止めない)。 */
  approveWarningMessages: string[]
  /** 差戻しダイアログに出す承認者でない・自己承認の再確認 (AC-BSP-098)。 */
  rejectWarningMessages: string[]
}

/**
 * 通常入札の承認 / 差戻し (PENDING → APPROVED / REJECTED、 AC-BSP-094) の状態と操作。
 *
 * 承認案件一覧画面 (`admin/operation/approval/approval-tasks.md`) が Phase 1 時点で
 * 動作保証対象外のため、 スポット入札画面が承認/差戻し操作の唯一の経路
 * (spec `spot-bidding.md:171`)。 page (`SpotBidding/index.tsx`) から切り出したのは、
 * 「承認者としての判断」 が申請者側の下書き編集フローと独立した関心であり、 確認 modal
 * 用の集計・再検証・確定時ガードまで一式で閉じられるため (= この hook を消すと承認/差戻し
 * 導線だけが丸ごと消え、 下書き編集は影響を受けない)。
 *
 * 承認者でない人・申請者本人も承認 / 差戻しでき、 確認 modal で再確認する (AC-BSP-098)。
 */
export const useSpotApprovalDecision = ({
  pendingRequests,
  bids,
  volumeTotalRows,
  isBlockVolumeUnresolved,
  limits,
  isLimitsError,
  isStateUnresolved,
  isReadonly,
  phase,
  targetDate,
  onTransitionStart,
  onDecided,
}: Params): SpotApprovalDecision => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { mutateAsync: transitionAsync, isPending: isTransitioning } =
    useTransitionBiddingSpotApprovalState()

  // entity ではなく id で保持する (= modal を開いたまま invalidate/refetch が走っても
  // stale な expectedUpdatedAt を握り続けないため。 Params.pendingRequests 参照)。
  const [approvingRequestId, setApprovingRequestId] = useState<
    string | undefined
  >(undefined)
  const [rejectingRequestId, setRejectingRequestId] = useState<
    string | undefined
  >(undefined)

  // 承認確認 modal の集計・違反明細は承認対象 request の札のみ (AC-BSP-094 / AC-BSP-082)。
  const approvingBids = useMemo(
    () => selectRequestBids(bids, approvingRequestId),
    [bids, approvingRequestId],
  )
  const summary = useMemo(
    () => summarizeSpotBidRows(approvingBids),
    [approvingBids],
  )
  const areaBreakdown = useMemo(
    () => buildSpotAreaBreakdown(approvingBids),
    [approvingBids],
  )
  // AC-BSP-097: 承認申請時点 (AC-BSP-008) 以降に積み増されて申請済になった札
  // (AC-BSP-079 の追加入札) を含めて再検証する。 母集合の作り分け (札単位の判定は対象
  // セット / 集計判定は受渡日の有効札全体、 未申請の下書きは含めない) は
  // buildSpotViolationSections / selectRevalidationBids の JSDoc 参照。
  const revalidationBids = useMemo(() => selectRevalidationBids(bids), [bids])
  const { violationSections, warningSections } = useMemo(() => {
    const checks = buildSpotApprovalChecks(
      approvingBids,
      {
        revalidationBids,
        volumeTotalRows,
        limits,
        isLimitsUnavailable: isLimitsError,
        isBlockVolumeUnresolved,
      },
      t,
    )
    // 単件は判定できない節も違反と同じく確定を止める。
    return {
      violationSections: [
        ...checks.violationSections,
        ...checks.unverifiedSections,
      ],
      warningSections: checks.warningSections,
    }
  }, [
    approvingBids,
    isBlockVolumeUnresolved,
    isLimitsError,
    limits,
    revalidationBids,
    t,
    volumeTotalRows,
  ])
  const hasViolation = violationSections.some((s) => s.details.length > 0)

  const approverIdentity = useApproverIdentity()
  const warningMessagesFor = (
    requestId: string | undefined,
    action: 'approve' | 'reject',
  ): string[] => {
    const request = pendingRequests.find((r) => r.id === requestId)
    return request === undefined
      ? []
      : buildApproverConfirmNotices(approverIdentity(request), action, t)
  }

  /**
   * 承認 / 差戻し の可否 policy (AC-BSP-058 / AC-BSP-099 / 明細未解決)。 導線側は
   * `decisionDisabledReason` (= バナーの disabled + tooltip)、 確定時は
   * `ensureDecisionAllowed` が同じ関数をサーバー由来の現在時刻で引き直す。
   *
   * 導線の無効化 (banner の decisionDisabledReason) だけでは、 modal を
   * 開いたまま別ユーザーがケースを完了させた / ケース取得が失敗して isReadonly に
   * 倒れた / 締切を跨いだ場合に APPROVE / REJECT が飛んでしまう。
   *
   * 締切判定は分単位 tick (useNowByMinute) 由来の phase ではなくサーバー由来の現在時刻で
   * 引き直す。 導線に使う phase は最大 1 分古く、 締切直後の 1 分間は
   * ボタンが出たままになるため (= 申請取消 AC-BSP-055c / 再編集 AC-BSP-056c と同じ形)。
   *
   * 明細 / 一覧 Get が未解決の間も中断する (AC-BSP-097)。 未解決の request の札は
   * `spotApprovalRequests` から静かに脱落し `bids` に現れないため、 再検証の母集合
   * (AC-BSP-079 で積み増された追加入札を含む受渡日の有効札全体) が欠けた状態で
   * 「違反 0 件」 になる。 対象 request 自身の明細が落ちた場合はさらに、 確認 modal が
   * 札 0 件・違反 0 件へ縮退したまま「承認する」 が活性で残る。 入札制限の取得失敗
   * (isLimitsError) を「違反なし」 に潰さないのと同じ理由で、 検証が成立していない状態で
   * 承認を通さない (= 他の spot 書込経路 persistDraftBids / 承認申請 / 全削除 /
   * JEPX 提出、 およびブロック側 useBlockApprovalDecision と同じ fail-closed)。
   *
   * ただし isReadonly の方は client 側の表示制御であって security 境界ではない:
   * URL の `?workflowId=` 起点でしか true にならない (useCurrentWorkflowCase)。
   * backend は case_id 付きで届いた遷移ならケース状態を検証して拒否する (AC-BSP-058)
   * が、 case_id を省略した request は通す (#8265) ので、 `?workflowId=` を
   * 外して開けば素通しできる。 申請取消 (AC-BSP-055) / 再編集 (AC-BSP-056) の既存
   * ガードも同じ構造。
   *
   * 中断する時は確認 modal も閉じる (= 開いたままだと同じ操作を繰り返させるだけ)。
   */
  const blockedReasonFor = (phaseAt: SpotMarketPhase): string | undefined => {
    if (isReadonly) {
      // undefined は「できる」 を意味するので、 取得中・取得失敗の fail-closed 中も同じ文で塞ぐ。
      return t('common:workflowLock.readonlyReason')
    }
    if (!isSpotBeforeDeadline(phaseAt)) {
      return t('page:biddingSpot.approvalAction.decisionDeadlinePassed')
    }
    if (isStateUnresolved) {
      return t('page:biddingSpot.serverError.detailUnresolved')
    }
    return undefined
  }
  // 導線側 (バナーの disabled + tooltip) は分単位 tick の phase で判定する。
  const decisionDisabledReason = blockedReasonFor(phase)
  const ensureDecisionAllowed = (): boolean => {
    const reason = blockedReasonFor(calcSpotPhase(serverNow(), targetDate))
    if (reason === undefined) return true
    showErrorToast({ title: reason })
    setApprovingRequestId(undefined)
    setRejectingRequestId(undefined)
    return false
  }

  /**
   * APPROVE / REJECT の共通処理。 対象は確定時に `pendingRequests` から解決し、 楽観ロック
   * 基準値はその最新 `updatedAt` にする。
   * 成功時の一覧再取得は useTransitionBiddingSpotApprovalState の invalidate に委譲する。
   */
  const transition = async (
    targetId: string | undefined,
    action:
      | typeof BiddingLifecycleAction.APPROVE
      | typeof BiddingLifecycleAction.REJECT,
    reason: string | undefined,
    successKey: string,
    clearTarget: () => void,
  ): Promise<void> => {
    if (targetId === undefined) return
    if (!ensureDecisionAllowed()) return
    // 背景再取得で対象が PENDING でなくなっていた (= 他ユーザーが承認/差戻し/取消済み)
    // 場合は中断する (= ブロック側と同じガード)。
    const target = pendingRequests.find((r) => r.id === targetId)
    if (target === undefined) {
      showErrorToast({ title: t('page:biddingSpot.serverError.aborted') })
      clearTarget()
      return
    }
    onTransitionStart?.()
    try {
      await transitionAsync({
        id: target.id,
        action,
        reason,
        expectedUpdatedAt: target.updatedAt,
      })
      showSuccessToast({ title: t(successKey) })
      clearTarget()
      // 対象への絞り込みは役目を終える (= PENDING でなくなった request に絞られたまま
      // 残ると、 承認済 / 差戻し済の札だけが表示され続ける)。
      onDecided?.(target.id)
    } catch (err) {
      // 楽観ロック競合 (Aborted) 等で失敗した場合、 一覧・明細を invalidate して実状態を
      // 再取得する (= ブロック側 useBlockApprovalDecision の catch と対称)。
      void invalidateSpotApprovalRequestQueries(queryClient)
      showErrorToast({ title: translateSpotWriteError(err, t) })
    }
  }

  return {
    approvingRequestId,
    rejectingRequestId,
    openApprove: (request) => {
      setApprovingRequestId(request.id)
    },
    openReject: (request) => {
      setRejectingRequestId(request.id)
    },
    closeApprove: () => {
      setApprovingRequestId(undefined)
    },
    closeReject: () => {
      setRejectingRequestId(undefined)
    },
    approve: () =>
      transition(
        approvingRequestId,
        BiddingLifecycleAction.APPROVE,
        undefined,
        'page:biddingSpot.approvalAction.approveSuccess',
        () => {
          setApprovingRequestId(undefined)
        },
      ),
    // 差戻し理由は必須にしている。 AC-BSP-094 は spec PR #6535 で「必須入力」 に統一され、
    // AC-BSP-063 [P0] の未入力エラー (「差戻し理由を入力してください」) と整合した。
    // backend も空文字を CodeInvalidArgument で拒否する。
    reject: (reason: string) =>
      transition(
        rejectingRequestId,
        BiddingLifecycleAction.REJECT,
        reason,
        'page:biddingSpot.approvalAction.rejectSuccess',
        () => {
          setRejectingRequestId(undefined)
        },
      ),
    isTransitioning,
    decisionDisabledReason,
    decisionBlockedReasonAt: blockedReasonFor,
    summary,
    areaBreakdown,
    violationSections,
    hasViolation,
    warningSections,
    approveWarningMessages: warningMessagesFor(approvingRequestId, 'approve'),
    rejectWarningMessages: warningMessagesFor(rejectingRequestId, 'reject'),
  }
}
